import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Field, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import {
    bundleIcon,
    LineHorizontal1Regular,
    MoreVerticalFilled,
    MoreVerticalRegular,
    TaskListLtrRegular,
    TextBoldRegular,
    TextBulletListLtrRegular,
    TextHeader1Regular,
    TextHeader2Regular,
    TextHeader3Regular,
    TextItalicRegular,
    TextNumberListLtrRegular,
    TextParagraphRegular,
    TextQuoteRegular,
    TextStrikethroughRegular,
    TextUnderlineRegular,
} from '@fluentui/react-icons'
import OverflowToolbarPanel, { HammockToolbarButtonData, useOverflowToolbarPanelStyles } from '../Overflow/OverflowToolbarPanel'
import { useTranslation } from 'react-i18next'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import { TaskItem } from '@tiptap/extension-list'
import { findValue, FormikSubFieldProps, useFieldPlaceholder, useFormikFieldStyles } from './FormikField'
import { linkifyPlainText, richTextExtensions } from '../../v2/components/Text/richTextExtensions'

const MoreIcon = bundleIcon(MoreVerticalFilled, MoreVerticalRegular)

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    containerFocused: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    // Overflow measures the toolbar against this element, so it has to be allowed to shrink:
    // a flex child without minWidth 0 keeps its content width and nothing ever overflows.
    toolbarContainer: {
        minWidth: 0,
    },
    toolbar: {
        padding: '2px 4px',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
        borderTopLeftRadius: tokens.borderRadiusMedium,
        borderTopRightRadius: tokens.borderRadiusMedium,
    },
    editor: {
        padding: '6px 10px',
        minHeight: '60px',
        cursor: 'text',
        '& .ProseMirror': {
            outline: 'none',
            minHeight: '48px',
        },
        '& .ProseMirror p': {
            margin: 0,
        },
        '& .ProseMirror p + p': {
            marginTop: tokens.spacingVerticalXS,
        },
        '& .ProseMirror ul, & .ProseMirror ol': {
            margin: '4px 0',
            paddingLeft: '24px',
        },
        // A task list carries its own checkbox, so it drops the bullet and the indent the marker needed.
        // TaskItem renders <li><label><input><span></span></label><div>text</div></li>.
        '& .ProseMirror ul[data-type="taskList"]': {
            listStyle: 'none',
            paddingLeft: 0,
        },
        '& .ProseMirror ul[data-type="taskList"] li': {
            display: 'flex',
            alignItems: 'flex-start',
            gap: tokens.spacingHorizontalXS,
        },
        '& .ProseMirror ul[data-type="taskList"] li > label': {
            display: 'flex',
            alignItems: 'center',
            // The line box of the text next to it, so the checkbox lines up with the first line rather than the top.
            height: tokens.lineHeightBase300,
        },
        // Keeps the checkbox on the brand colour in both themes without a custom node view.
        '& .ProseMirror ul[data-type="taskList"] input[type="checkbox"]': {
            accentColor: tokens.colorBrandBackground,
            margin: 0,
        },
        '& .ProseMirror ul[data-type="taskList"] li > div': {
            flexGrow: 1,
            minWidth: 0,
        },
        // The placeholder extension only marks the empty node; drawing it is up to us. Same recipe as the tiptap
        // documentation: a floating pseudo element without height, so it does not push the first line down.
        '& .ProseMirror p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            color: tokens.colorNeutralForeground4,
            float: 'left',
            height: 0,
            pointerEvents: 'none',
        },
        // A quote is marked by a line in the margin, not by a box: the same shape as the read-only view in
        // RichText, so a note does not change appearance when it is opened.
        '& .ProseMirror blockquote': {
            margin: `${tokens.spacingVerticalS} 0 ${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            paddingLeft: tokens.spacingHorizontalM,
            borderLeft: `2px solid ${tokens.colorNeutralStroke1}`,
        },
        '& .ProseMirror a': {
            color: tokens.colorBrandForegroundLink,
            textDecoration: 'underline',
        },
    },
})

export interface FormikRichTextFieldProps<Values> extends FormikSubFieldProps<unknown, Values> {
    /**
     * Turns typed and pasted URLs into clickable links. Off by default, because a description travels into
     * customer templates through `|raw`. Switch it on per usage site, and switch it on for the matching
     * RichText as well, otherwise the reader only gets the label text of the link.
     */
    allowLinks?: boolean
}

export default function FormikRichTextField<Values>(props: FormikRichTextFieldProps<Values>): ReactElement {
    const styles = useStyles()
    const overflowStyles = useOverflowToolbarPanelStyles()
    const fieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()
    const { values, errors, setFieldValue, setFieldTouched } = useFormikContext<Values>()

    const currentValue = useMemo<string>(() => findValue(values, props.fieldName as string) ?? '', [values, props.fieldName])
    // Same source as every other field: placeholderKey, with the required suffix behind it when the field carries
    // no label to hang the asterisk on.
    const placeholder = useFieldPlaceholder(props)
    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [errors, props.fieldName])
    const validationState = useMemo<'error' | 'none'>(
        () => (findValue(errors, props.fieldName as string) ? 'error' : 'none'),
        [errors, props.fieldName],
    )

    const editor = useEditor({
        // This extension list is what sanitizes the field: getHTML() serializes a ProseMirror document, which can
        // only hold nodes and marks from the schema, and everything entering the editor (typing, paste, setContent)
        // is parsed through that same schema first. A DOMPurify pass over the result filtered nothing the schema had
        // not already filtered - it only drifted out of sync with the extensions and silently ate the user's markup.
        extensions: richTextExtensions({
            taskItem: TaskItem.configure({ nested: true }),
            allowLinks: props.allowLinks,
            placeholder,
        }),
        content: currentValue,
        onUpdate: ({ editor }) => {
            setFieldValue(props.fieldName as string, editor.getHTML(), false)
        },
        onBlur: ({ editor }) => {
            // A URL typed as the last thing in the field is still plain text: autolink only fires on the
            // whitespace after a word. Leaving the field is the last moment to make it a link anyway.
            linkifyPlainText(editor)
            setFieldValue(props.fieldName as string, editor.getHTML(), false)
            setFieldTouched(props.fieldName as string, true)
        },
    })

    // What the caret sits in right now. This has to go through useEditorState: since tiptap v3 the component
    // is not re-rendered on every transaction, so reading editor.isActive() while rendering left the buttons
    // showing the formatting of wherever the caret happened to be on the previous render.
    const activeStates = useEditorState({
        editor,
        selector: ({ editor }) => ({
            // Same story for the focus ring around the field.
            focused: !!editor?.isFocused,
            bold: !!editor?.isActive('bold'),
            italic: !!editor?.isActive('italic'),
            underline: !!editor?.isActive('underline'),
            strikethrough: !!editor?.isActive('strike'),
            bulletList: !!editor?.isActive('bulletList'),
            orderedList: !!editor?.isActive('orderedList'),
            taskList: !!editor?.isActive('taskList'),
            // Inside a list item or a quote the text is still a paragraph, so only call it "normal text"
            // when no block style wraps it - otherwise two buttons light up at once.
            paragraph:
                !!editor?.isActive('paragraph') &&
                !editor?.isActive('bulletList') &&
                !editor?.isActive('orderedList') &&
                !editor?.isActive('taskList') &&
                !editor?.isActive('blockquote'),
            heading1: !!editor?.isActive('heading', { level: 1 }),
            heading2: !!editor?.isActive('heading', { level: 2 }),
            heading3: !!editor?.isActive('heading', { level: 3 }),
            blockquote: !!editor?.isActive('blockquote'),
            code: !!editor?.isActive('code'),
            codeBlock: !!editor?.isActive('codeBlock'),
        }),
    })

    useEffect(() => {
        if (!editor) {
            return
        }
        if (currentValue === editor.getHTML()) {
            return
        }
        editor.commands.setContent(currentValue || '')
    }, [currentValue, editor])

    if (!editor) {
        return <></>
    }

    const { focused, ...activeMarks } = activeStates ?? {}
    const activeValues = new Set(Object.keys(activeMarks).filter(value => activeMarks[value]))

    // Four menu buttons instead of fourteen toolbar buttons: even in the Outlook task pane the whole set stays
    // reachable, and a group only falls into the overflow menu as a whole. A parent lights up when one of its
    // children is active, so the caret's formatting is still readable without opening a menu.
    const groups: HammockToolbarButtonData[] = [
        { value: 'bold', titleKey: 'richtext.bold', icon: <TextBoldRegular />, onClick: () => editor.chain().focus().toggleBold().run() },
        {
            value: 'italic',
            titleKey: 'richtext.italic',
            icon: <TextItalicRegular />,
            onClick: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            value: 'underline',
            titleKey: 'richtext.underline',
            icon: <TextUnderlineRegular />,
            onClick: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
            value: 'lists',
            titleKey: 'richtext.group.lists',
            icon: <TextBulletListLtrRegular />,
            childButtons: [
                {
                    value: 'bulletList',
                    titleKey: 'richtext.bulletList',
                    icon: <TextBulletListLtrRegular />,
                    onClick: () => editor.chain().focus().toggleBulletList().run(),
                },
                {
                    value: 'orderedList',
                    titleKey: 'richtext.orderedList',
                    icon: <TextNumberListLtrRegular />,
                    onClick: () => editor.chain().focus().toggleOrderedList().run(),
                },
                {
                    value: 'taskList',
                    titleKey: 'richtext.taskList',
                    icon: <TaskListLtrRegular />,
                    onClick: () => editor.chain().focus().toggleTaskList().run(),
                },
            ],
        },
        {
            value: 'headings',
            titleKey: 'richtext.group.headings',
            icon: <TextHeader1Regular />,
            childButtons: [
                {
                    value: 'heading1',
                    titleKey: 'richtext.heading1',
                    icon: <TextHeader1Regular />,
                    onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
                },
                {
                    value: 'heading2',
                    titleKey: 'richtext.heading2',
                    icon: <TextHeader2Regular />,
                    onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
                },
                {
                    value: 'heading3',
                    titleKey: 'richtext.heading3',
                    icon: <TextHeader3Regular />,
                    onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
                },
            ],
        },
        {
            value: 'other',
            titleKey: 'richtext.group.other',
            icon: <MoreIcon />,
            childButtons: [
                {
                    value: 'paragraph',
                    titleKey: 'richtext.paragraph',
                    icon: <TextParagraphRegular />,
                    onClick: () => editor.chain().focus().setParagraph().run(),
                },
                {
                    value: 'blockquote',
                    titleKey: 'richtext.blockquote',
                    icon: <TextQuoteRegular />,
                    onClick: () => editor.chain().focus().toggleBlockquote().run(),
                },
                {
                    value: 'horizontalRule',
                    titleKey: 'richtext.horizontalRule',
                    icon: <LineHorizontal1Regular />,
                    onClick: () => editor.chain().focus().setHorizontalRule().run(),
                },
                {
                    value: 'strikethrough',
                    titleKey: 'richtext.strikethrough',
                    icon: <TextStrikethroughRegular />,
                    onClick: () => editor.chain().focus().toggleStrike().run(),
                },
            ],
        },
    ]

    groups
        .filter(group => activeValues.has(group.value) || group.childButtons?.some(child => activeValues.has(child.value)))
        .forEach(group => activeValues.add(group.value))

    return (
        <Field
            label={props.renderLabel ?? (props.labelKey ? translate(props.labelKey, { ...props.labelKeyProps }) : undefined)}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey, { ...props.hintKeyProps }) : undefined)}
            validationMessage={validationMessage}
            validationState={validationState}
            className={mergeClasses(props.className, !props.renderLabel && !props.labelKey && fieldStyles.noLabel)}
        >
            <div className={mergeClasses('fui-Textarea', styles.container, focused && styles.containerFocused)}>
                <OverflowToolbarPanel
                    buttons={groups}
                    containerClassName={styles.toolbarContainer}
                    toolbarProps={{ size: 'small', className: styles.toolbar }}
                    getToolbarButtonProps={button => ({ appearance: activeValues.has(button?.value) ? 'primary' : 'subtle' })}
                    getMenuItemProps={button => ({ className: activeValues.has(button.value) ? overflowStyles.selectedMenuItem : undefined })}
                    overflowButtonLabelKey={'base.item.label'}
                />
                <div className={styles.editor} onClick={() => editor.chain().focus().run()} onKeyDown={e => e.stopPropagation()}>
                    <EditorContent editor={editor} />
                </div>
            </div>
        </Field>
    )
}
